import { useState } from 'react';
import type { ReactNode } from 'react';
import { useLive, useRuntime } from './context';
import { addDays, formatClock, formatDayLong, isoDayIn, zonedWallClockToIso } from '../mirror/time';
import { BookSheet } from './BookSheet';
import type { BookSheetPrefill } from './BookSheet';
import { sourceWord } from './Today';

/**
 * Calendar — one day at a time, per resource, with a date picker.
 *
 * The grid is drawn from the tenant's **mirrored** opening hours, not from
 * availability. That is the deliberate line: an empty cell here means "nothing
 * is booked in the copy of the book this device holds", which is a claim the
 * device can honestly make offline. It does **not** mean "this is free" — only
 * `GET /v1/availability` can say that, because only `packages/scheduling` knows
 * about buffers, holds, blackouts and capacity, and there is exactly one
 * implementation of it (dev.md rule 3).
 *
 * So tapping an empty cell opens the booking form pre-filled with that time
 * rather than asserting the slot is free, and the server settles it — with the
 * enriched `409` if the answer is no, which becomes a task. The calendar being
 * *usefully* wrong offline and *honestly* wrong is the whole trade of §8.
 */
export function CalendarView(): ReactNode {
  const runtime = useRuntime();
  const session = useLive(() => runtime.mirror.session(), null);
  const timezone = session?.tenant.timezone ?? 'UTC';

  // Derived until the owner picks a day, rather than seeded once in `useState`:
  // the session (and with it the tenant's timezone) arrives from IndexedDB a
  // tick after the first render, and a calendar seeded in UTC would open on
  // tomorrow for the last four hours of every day in the Caribbean.
  const [picked, setPicked] = useState<string | null>(null);
  const day = picked ?? isoDayIn(timezone, runtime.now());
  const setDay = setPicked;
  const [booking, setBooking] = useState<BookSheetPrefill | null>(null);

  const appointments = useLive(() => runtime.mirror.day(day), [], [day]);
  const resources = useLive(() => runtime.mirror.resources(), []);
  const hours = useLive(() => runtime.mirror.hours(), null);

  const weekday = weekdayIndex(day);
  const dayHours = hours?.hours.find((h) => h.weekday === weekday);
  const slots = gridFor(dayHours?.closed === true ? [] : (dayHours?.windows ?? []));

  return (
    <section className="view">
      <div className="view__header">
        <div className="date-picker">
          <button type="button" className="button" onClick={() => setDay(addDays(day, -1))}>
            ‹
          </button>
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          <button type="button" className="button" onClick={() => setDay(addDays(day, 1))}>
            ›
          </button>
        </div>
        <h1>{formatDayLong(day, timezone)}</h1>
      </div>

      {dayHours?.closed === true ? <p className="empty">Closed on this day.</p> : null}

      <div className="calendar" role="grid">
        <div className="calendar__head" role="row">
          <div className="calendar__corner" role="columnheader" />
          {resources
            .filter((r) => r.active)
            .map((r) => (
              <div key={r.resource_id} className="calendar__resource" role="columnheader">
                {r.name}
              </div>
            ))}
        </div>

        {slots.map((slot) => (
          <div key={slot} className="calendar__row" role="row">
            <div className="calendar__time" role="rowheader">
              {slot}
            </div>
            {resources
              .filter((r) => r.active)
              .map((r) => {
                const booked = appointments.find(
                  (a) =>
                    a.slot.resource_id === r.resource_id &&
                    a.status !== 'cancelled' &&
                    coversSlot(a.slot.start, a.slot.end, day, slot, timezone),
                );
                if (booked !== undefined) {
                  const isStart = formatSlotKey(booked.slot.start, timezone) === slot;
                  return (
                    <div
                      key={r.resource_id}
                      role="gridcell"
                      className={`calendar__cell calendar__cell--booked calendar__cell--${booked.source}`}
                      title={`${booked.client.display_name} · ${booked.service_name}`}
                    >
                      {isStart ? (
                        <>
                          <strong>{booked.client.display_name}</strong>
                          <small>
                            {booked.service_name} · {sourceWord(booked.source)}
                          </small>
                        </>
                      ) : null}
                    </div>
                  );
                }
                return (
                  <button
                    key={r.resource_id}
                    type="button"
                    role="gridcell"
                    className="calendar__cell calendar__cell--open"
                    onClick={() =>
                      setBooking({
                        startIso: zonedWallClockToIso(`${day}T${slot}`, timezone),
                        resourceId: r.resource_id,
                        source: 'staff',
                      })
                    }
                  >
                    <span className="visually-hidden">
                      Book {r.name} at {slot}
                    </span>
                  </button>
                );
              })}
          </div>
        ))}
      </div>

      {slots.length === 0 && dayHours?.closed !== true ? (
        <p className="empty">No opening hours are set for this day yet — see Settings.</p>
      ) : null}

      <ul className="day-list day-list--compact">
        {appointments.map((a) => (
          <li key={a.appointment_id} className="day-row">
            <div className="day-row__time">
              <strong>{formatClock(a.slot.start, timezone)}</strong>
            </div>
            <div className="day-row__who">
              <strong>{a.client.display_name}</strong>
              <small>{a.service_name}</small>
            </div>
            <span className={`badge badge--source-${a.source}`}>{sourceWord(a.source)}</span>
          </li>
        ))}
      </ul>

      {booking !== null ? (
        <BookSheet
          prefill={booking}
          onDone={() => setBooking(null)}
          onCancel={() => setBooking(null)}
        />
      ) : null}
    </section>
  );
}

/** Monday-first, matching `DayHours.weekday` and LoveSpa's `meta.work_hours`. */
export function weekdayIndex(isoDay: string): number {
  const at = new Date(`${isoDay}T12:00:00Z`);
  return (at.getUTCDay() + 6) % 7;
}

/** Half-hour rows across every open window of the day. */
export function gridFor(windows: { start: string; end: string }[], stepMin = 30): string[] {
  const slots: string[] = [];
  for (const window of windows) {
    for (let m = toMinutes(window.start); m < toMinutes(window.end); m += stepMin) {
      slots.push(toClock(m));
    }
  }
  return [...new Set(slots)].sort();
}

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return h * 60 + m;
}

function toClock(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatSlotKey(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

/** Half-open `[start, end)`, the same rule the database enforces. */
function coversSlot(
  startIso: string,
  endIso: string,
  day: string,
  slot: string,
  timezone: string,
): boolean {
  const slotStart = Date.parse(zonedWallClockToIso(`${day}T${slot}`, timezone));
  return Date.parse(startIso) <= slotStart && slotStart < Date.parse(endIso);
}
